import { effectivenessStats, PlacementPrediction, rankedByEfficiency } from './effectiveness-data';

// Effectiveness, not just efficiency. Jay Pattisall's thesis is that agencies
// mistake faster execution (efficiency) for better outcomes (effectiveness). This
// panel answers "what changed about the OUTCOME?": Tinuiti's patented streaming-ML
// model predicts a cost-per-conversion for every placement, and the plan
// concentrates budget where conversions are cheapest — not just where reach is.
// Shown as an illustrative snapshot from a real, UNNAMED advertiser, because
// ELEVATE (brand-new, no delivery) has no model output of its own yet.

const usd = (value: number): string =>
  `$${value.toLocaleString('en-US', { maximumFractionDigits: value < 10 ? 2 : 0 })}`;

export const EffectivenessPanel = () => {
  const stats = effectivenessStats();
  const rows = rankedByEfficiency();

  return (
    <div className='elevate-eff'>
      <div className='elevate-eff-head'>
        <h2 className='elevate-eff-title'>
          Effectiveness — the model behind the buy{' '}
          <span className='elevate-eff-tag'>illustrative</span>
        </h2>
        <p className='elevate-eff-sub'>
          Every placement carries a prediction from Tinuiti&apos;s patented streaming-ML model:
          expected <strong>cost per conversion</strong> and conversions per media dollar. The plan
          uses these to concentrate budget where conversions are cheapest — the effectiveness lever,
          not just cheaper reach.
        </p>
      </div>

      <div className='elevate-eff-stats'>
        <Stat value={usd(stats.best)} label='best predicted cost / conversion' />
        <Stat value={usd(stats.median)} label='median across this inventory' />
        <Stat
          value={`${stats.efficiencySpread.toFixed(0)}×`}
          label='spread, best vs. least efficient'
        />
      </div>

      <table className='elevate-eff-table'>
        <thead>
          <tr>
            <th>Placement</th>
            <th>CPM</th>
            <th>Pred. cost / conversion</th>
            <th>Pred. conversions / $</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <PredictionRow key={row.placement} row={row} />
          ))}
        </tbody>
      </table>

      <p className='elevate-eff-note'>
        Illustrative: a real snapshot of the stage-2 model for an <strong>unnamed</strong>{' '}
        advertiser — the same read-only diagnostics the planning agent consults to ground placement
        selection. ELEVATE has no delivery history yet, so this shows the capability, not
        ELEVATE&apos;s numbers; once ELEVATE is live, its own model output replaces this and closes
        the loop.
      </p>
    </div>
  );
};

interface StatProps {
  value: string;
  label: string;
}

const Stat = ({ value, label }: StatProps) => (
  <div className='elevate-eff-stat'>
    <span className='elevate-eff-stat-value'>{value}</span>
    <span className='elevate-eff-stat-label'>{label}</span>
  </div>
);

interface PredictionRowProps {
  row: PlacementPrediction;
}

const PredictionRow = ({ row }: PredictionRowProps) => (
  <tr>
    <td>{row.placement}</td>
    <td>{usd(row.cpm)}</td>
    <td>{usd(row.predCostPerConversion)}</td>
    <td>{row.predConversionsPerDollar.toFixed(1)}</td>
  </tr>
);
